import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../components/common/DataTable";
import api from "../services/api";
import { showToast } from "../helper/notifications";
import { useDebounce } from "../hook/useDebounce";

const LocationsGoogleBusinessPage = () => {
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Modal & Account State
  const [showImportModal, setShowImportModal] = useState(false);
  const [googleToken, setGoogleToken] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [fetchingAccounts, setFetchingAccounts] = useState(false);
  const [importing, setImporting] = useState(false);

  // Sorting State - Aligned with MySQL columns ('title', 'updated_at', etc.)
  const [sortConfig, setSortConfig] = useState({ key: "updated_at", direction: "desc" });

  const debouncedSearch = useDebounce(search, 500);

  // Fetch Locations Grid Data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/google-business/locations", {
        params: { 
          search: debouncedSearch, 
          page, 
          limit,
          sort_by: sortConfig.key,
          order: sortConfig.direction 
        },
      });
      setData(res.data?.data || []);
      setTotal(res.data?.pagination?.total || 0);
    } catch (error) {
      showToast("error", "Failed to load Google Business records");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page, limit, sortConfig]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch Stored Accounts when Modal opens
  const loadSavedAccounts = async () => {
    try {
      const res = await api.get("/google-business/accounts");
      if (res.data?.success) {
        const savedAccounts = res.data.accounts || [];
        setAccounts(savedAccounts);
        if (savedAccounts.length > 0) {
          setSelectedAccount(savedAccounts[0].accountId);
        }
      }
    } catch (error) {
      console.error("Could not fetch saved accounts from DB:", error);
    }
  };

  const handleOpenModal = () => {
    setShowImportModal(true);
    loadSavedAccounts();
  };

  // STEP 1: Call Google API to Fetch & Save Accounts
  const handleFetchAccounts = async () => {
    if (!googleToken.trim()) {
      showToast("error", "Please enter a valid Google OAuth Access Token first.");
      return;
    }

    setFetchingAccounts(true);
    try {
      const res = await api.post("/google-business/fetch-accounts", {
        accessToken: googleToken.trim(),
      });

      if (res.data?.success) {
        const fetchedAccounts = res.data.accounts || [];
        setAccounts(fetchedAccounts);
        if (fetchedAccounts.length > 0) {
          setSelectedAccount(fetchedAccounts[0].accountId);
        }
        showToast("success", `Found and saved ${fetchedAccounts.length} account(s)!`);
      }
    } catch (error) {
      showToast("error", error.response?.data?.error || "Failed to fetch accounts from Google.");
    } finally {
      setFetchingAccounts(false);
    }
  };

  // STEP 2: Import Locations for Selected Account
  const handleImportSubmit = async (e) => {
    e.preventDefault();
    if (!googleToken.trim()) {
      showToast("error", "Please enter a valid Google OAuth Access Token.");
      return;
    }

    if (!selectedAccount) {
      showToast("error", "Please select or fetch a Google Business account first.");
      return;
    }

    setImporting(true);
    try {
      const res = await api.post("/google-business/import-locations", {
        accessToken: googleToken.trim(),
        accountId: selectedAccount,
      });

      if (res.data?.success) {
        showToast("success", `Imported ${res.data.data.totalFetched} profile(s) successfully!`);
        setShowImportModal(false);
        setGoogleToken("");
        fetchData(); // Refresh grid
      }
    } catch (error) {
      showToast("error", error.response?.data?.error || "Failed to import locations.");
    } finally {
      setImporting(false);
    }
  };

  // Sync single location back to Google API
  const handleSyncToGoogle = async (row) => {
    showToast("info", `Syncing ${row.title || 'location'} to Google...`);
    try {
      await api.post("/google-business/sync-to-google", { location_id: row.id });
      showToast("success", "Pushed to Google successfully!");
      fetchData();
    } catch (error) {
      showToast("error", "Failed to sync location to Google");
    }
  };

  // Columns updated to match `google_business_locations` DB fields
  const columns = useMemo(() => [
    { 
      key: "store_code", 
      label: "Store Code", 
      className: "text-md fw-semibold text-primary-light", 
      sortable: true,
      render: (row) => row.store_code || "—"
    },
    { 
      key: "title", 
      label: "Business Name", 
      className: "text-md fw-semibold text-primary-light", 
      sortable: true 
    },
    { 
      key: "primary_category_name", 
      label: "Category", 
      className: "text-secondary-light fw-medium",
      sortable: true,
      render: (row) => row.primary_category_name || "—"
    },
    { 
      key: "city", 
      label: "City / State", 
      className: "text-secondary-light fw-medium",
      sortable: true,
      render: (row) => row.city ? `${row.city}${row.state ? `, ${row.state}` : ''}` : "—"
    },
    { 
      key: "open_status", 
      label: "Status",
      sortable: true,
      render: (row) => (
        <span className={`px-16 py-4 radius-4 fw-medium text-xs ${
          row.open_status === 'OPEN' ? 'bg-success-focus text-success-main' : 'bg-danger-focus text-danger-main'
        }`}>
          {row.open_status || 'OPEN'}
        </span>
      )
    },
    { 
      key: "updated_at", 
      label: "Last Synced", 
      className: "text-secondary-light text-sm",
      sortable: true,
      render: (row) => row.updated_at ? new Date(row.updated_at).toLocaleDateString() : "—"
    },
  ], []);

  return (
    <>
      {/* Header bar overlay with Import Button */}
      <div className="d-flex justify-content-between align-items-center mb-24">
        <div>
          <h4 className="mb-4">Google Business Profiles</h4>
          <p className="text-secondary-light text-sm mb-0">Manage and synchronize business locations from Google API.</p>
        </div>
        <button
          className="btn btn-primary-600 radius-8 px-20 py-11 d-flex align-items-center gap-2"
          onClick={handleOpenModal}
        >
          <i className="ri-download-cloud-line text-xl" />
          Import from Google
        </button>
      </div>

      <DataTable
        title="Google Business Profile"
        data={data}
        loading={loading}
        searchTerm={search}
        onSearchChange={(v) => { setSearch(v); setPage(1); }}
        page={page}
        limit={limit}
        total={total}
        onPageChange={setPage}
        onLimitChange={(v) => { setLimit(v); setPage(1); }}
        showCheckbox={false}
        sortConfig={sortConfig}
        onSortChange={setSortConfig}
        columns={columns}
        actions={[
          {
            icon: "ri-refresh-line", 
            label: "Sync",
            className: "w-32-px h-32-px bg-info-focus text-info-main rounded d-flex align-items-center justify-content-center border-0",
            onClick: handleSyncToGoogle,
          },
          {
            icon: "ri-edit-line", 
            className: "w-32-px h-32-px bg-success-focus text-success-main rounded d-flex align-items-center justify-content-center border-0",
            onClick: (row) => navigate(`/google-business/${row.id}/edit`),
          },
        ]}
      />

      {/* Import Access Token & Account Selection Modal */}
      {showImportModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content radius-12 p-16">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold">Import Locations from Google Profile</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowImportModal(false)}
                ></button>
              </div>

              <div className="modal-body">
                <form onSubmit={handleImportSubmit}>
                  {/* Step 1: Token Input */}
                  <div className="mb-20">
                    <label className="form-label fw-semibold text-sm">
                      1. Enter Google OAuth Access Token
                    </label>
                    <textarea
                      rows="3"
                      className="form-control radius-8 font-monospace text-xs mb-8"
                      placeholder="Paste token here (starts with ya29...)"
                      value={googleToken}
                      onChange={(e) => setGoogleToken(e.target.value)}
                    />
                  </div>

                  {/* Step 2: Account Selection */}
                  <div className="mb-20">
                    <div className="d-flex justify-content-between align-items-center mb-8">
                      <label className="form-label fw-semibold text-sm mb-0">
                        2. Select Business Account
                      </label>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary radius-8 d-flex align-items-center gap-1"
                        onClick={handleFetchAccounts}
                        disabled={fetchingAccounts}
                      >
                        <i className="ri-refresh-line" />
                        {fetchingAccounts ? "Fetching..." : "Fetch Accounts from Google"}
                      </button>
                    </div>

                    {accounts.length > 0 ? (
                      <select
                        className="form-select radius-8 text-sm"
                        value={selectedAccount}
                        onChange={(e) => setSelectedAccount(e.target.value)}
                      >
                        {accounts.map((acc) => (
                          <option key={acc.accountId} value={acc.accountId}>
                            {acc.accountName} ({acc.accountId}) - [{acc.type}]
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="alert alert-warning text-xs mb-0 radius-8">
                        No accounts found in DB. Click <strong>"Fetch Accounts from Google"</strong> above to discover accounts for your token.
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="d-flex justify-content-end gap-2 mt-24">
                    <button
                      type="button"
                      className="btn btn-neutral-200 text-neutral-600 radius-8"
                      onClick={() => setShowImportModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary-600 radius-8 d-flex align-items-center gap-2"
                      disabled={importing || !selectedAccount}
                    >
                      {importing ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" />
                          Importing Profiles...
                        </>
                      ) : (
                        "Import Business Profiles"
                      )}
                    </button>
                  </div>
                </form>
              </div>

            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LocationsGoogleBusinessPage;